import { ConnectProto } from "@racoon/api";
import { Namespace, useT } from "@racoon/i18n";
import { FilterStateItem, useUrlFilters } from "@racoon/ui";
import { useEffect, useMemo, useRef, useState } from "react";
import type { LocationCriteria, YesNo } from "../lib/location-criteria";

export const usePickupLocationFilters = () => {
  const t = useT();
  // Same URL-backed filter state the other list pages use, so a filtered view
  // is shareable and survives a reload.
  const { sp, patch } = useUrlFilters();

  // The page opens on "Активные": the archive is rarely what a dispatcher needs first.
  const statusValue = sp.get("status") ?? ConnectProto.LocationStatus.ACTIVE.toString();
  // A URL carries any string. The app-access select reads "yes"/"no"; the "without route
  // group" checkbox can only show "yes", so anything else reads as unset rather than as a
  // filter the user cannot see or clear.
  const appAccessParam = sp.get("app_access");
  const appAccess: YesNo =
    appAccessParam === "yes" || appAccessParam === "no" ? appAccessParam : "";
  const typeId = sp.get("pickup_location_type_id") ?? "";
  const customerId = sp.get("customer_id") ?? "";
  const containerTypeId = sp.get("container_type_id") ?? "";
  const routeGroupId = sp.get("route_group_id") ?? "";
  const truckId = sp.get("truck_id") ?? "";
  const withoutRouteGroup = sp.get("without_route_group") === "yes" ? "yes" : "";
  const searchQuery = sp.get("search") ?? "";

  const onStateChange = (state: FilterStateItem) => patch({ status: state.value });
  const onFilterParamsChange = (key: string, value: string | null) => patch({ [key]: value });

  // The input updates at once; the URL — and with it the request — follows
  // after a pause, so typing does not fire a query per keystroke.
  const [searchValue, setSearchValue] = useState(searchQuery);
  useEffect(() => {
    setSearchValue(searchQuery);
  }, [searchQuery]);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);
  const onSearchChange = (value: string) => {
    setSearchValue(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      patch({ search: value.trim() ? value : null });
    }, 400);
  };

  const states: FilterStateItem[] = useMemo(
    () => [
      {
        key: t(Namespace.Enum, "ACTIVE"),
        value: ConnectProto.LocationStatus.ACTIVE.toString(),
      },
      {
        key: t(Namespace.Enum, "ARCHIVED"),
        value: ConnectProto.LocationStatus.INACTIVE.toString(),
      },
    ],
    [t],
  );
  const selectedState = states.find((state) => state.value === statusValue) ?? states[0];

  const criteria: LocationCriteria = useMemo(
    () => ({
      typeId,
      customerId,
      containerTypeId,
      appAccess,
      // The debounced URL value, not the live input: search runs on the server.
      search: searchQuery,
      routeGroupId,
      truckId,
      withoutRouteGroup,
    }),
    [
      typeId,
      customerId,
      containerTypeId,
      appAccess,
      searchQuery,
      routeGroupId,
      truckId,
      withoutRouteGroup,
    ],
  );

  return {
    selectedState,
    states,
    onStateChange,
    /**
     * Brings a deep-linked location into the list in one URL update: selects the tab of
     * its status and clears the filters that would hide it (see `filtersExcluding`).
     */
    focusLocation: (status: ConnectProto.LocationStatus, clearKeys: string[]) =>
      patch({
        status: String(status),
        ...Object.fromEntries(clearKeys.map((key) => [key, null])),
      }),
    /** Drops the `highlightLocation` param once its link has been followed, and only it. */
    clearHighlight: () => patch({ highlightLocation: null }),
    onFilterParamsChange,
    onSearchChange,
    searchValue,
    criteria,
  };
};
